<script setup>
import { Head, router, usePage } from '@inertiajs/vue3';
import { computed, inject, onMounted, ref, watch } from 'vue';
import BinCard from '@/Pages/Pick/Partials/BinCard.vue';
import {
    mdiArrowLeft,
    mdiArrowLeftThick,
    mdiArrowRight,
    mdiBarcodeScan,
    mdiCheckOutline,
    mdiInformation,
    mdiPlus,
} from '@mdi/js';
import PickListModal from '@/Pages/Pick/Partials/PickListModal.vue';
import usePickScans from '@/lib/composables/pick-scans.js';
import { collect } from 'collect.js';
import AppBarActionsTeleport from '@/Components/AppBarActionsTeleport.vue';
import LinkVuetify from '@/Components/LinkVuetify.vue';
import PackageExpansionPanel from '@/Pages/Pick/Partials/PackageExpansionPanel.vue';
import useBarcodeScanner from '@/lib/composables/barcode-scanner.js';
import QuantityModal from '@/Pages/Pick/Partials/QuantityModal.vue';
import { trans as $t } from 'laravel-vue-i18n';
import { useDisplay } from 'vuetify';
import PrintOptions from '@/Pages/Pick/Partials/PrintOptions.vue';
import useFullyScanner from '@/lib/composables/fully-scanner.js';
import {
    startClock,
    timeData,
    stopClock,
} from '@/lib/services/clock-service.js';
import AlarmModal from '@/Components/AlarmModal.vue';
import PickTicketModal from '@/Pages/Pick/Partials/PickTicketModal.vue';

const flash = inject('flash');

const page = usePage();

onMounted(() => {
    if (page.props.created) {
        startClock({
            activity: page.props.pick_activity_id,
            ean: page.props.pick_list.ean_code,
            force: true,
        });
    }
    page.props.created = false;
});

const { smAndDown: mobile } = useDisplay();
const currentPackageId = ref(page.props.pick_list.current_pick_package_id);
const currentPackageIdentifier = ref(
    page.props.pick_list.current_package.identifier,
);

const errorMessage = ref(null);
const printer = ref(page.props.selected_printer);

const {
    scan,
    scans,
    loading,
    transactionsPending,
    pauseTransactions,
    resumeTransactions,
    submitTransactions,
    errorMessage: printingErrorMessage,
} = usePickScans(
    page.props.pick_list.id,
    currentPackageId,
    page.props.pick_list.scans,
    printer,
);

const pauseKeydownEventListener = inject('pauseCaptureKeydown');

const { result: fullyResult, scan: fullyScan, isFully } = useFullyScanner();
const { onBarcodeScanned } = useBarcodeScanner({
    pauseEventListener: pauseKeydownEventListener,
});

const binsById = computed(() => {
    return page.props.pick_list.lines.reduce((acc, bin) => {
        acc[bin.id] = bin;
        return acc;
    }, {});
});
const scansById = computed(() => {
    return collect(scans.value).groupBy('pick_list_line_id');
});

const currentBinIndex = ref(null);
for (let i = 0; i < page.props.pick_list.lines.length; i++) {
    const line = page.props.pick_list.lines[i];
    if (line.quantity !== scansById.value.get(line.id)?.sum('quantity') ?? 0) {
        currentBinIndex.value = i;
        break;
    }

    if (i === page.props.pick_list.lines.length - 1) {
        currentBinIndex.value = i;
    }
}

const currentBin = computed(() => {
    return page.props.pick_list.lines[currentBinIndex.value];
});

const previousBin = computed(() => {
    if (currentBinIndex.value === 0) {
        return null;
    }
    return page.props.pick_list.lines[currentBinIndex.value - 1];
});
const nextBin = computed(() => {
    if (currentBinIndex.value === page.props.pick_list.lines.length - 1) {
        return null;
    }
    return page.props.pick_list.lines[currentBinIndex.value + 1];
});

const itemBarcode = computed(() => {
    return currentBin.value.item.barcodes.find(
        (barcode) => barcode['Unit of Measure'] === currentBin.value.unit,
    )?.['Cross-Reference No_'];
});
const scanned = ref(false);
const itemScanned = ref(false);
const binScanned = ref(false);
const scanFailed = ref(false);
const onScanned = (barcode) => {
    let codeFound = false;

    if (itemBarcode.value) {
        if (barcode === itemBarcode.value) {
            itemScanned.value = true;
            codeFound = true;
        }
    } else {
        itemScanned.value = true;
    }

    if (!codeFound && barcode === currentBin.value.ean_code) {
        binScanned.value = true;
        codeFound = true;
    }
    scanned.value = itemScanned.value && binScanned.value;

    scanFailed.value = !codeFound;
};
watch(scanFailed, (value) => {
    if (value) {
        console.error('scan failed', {
            itemBarcode: itemBarcode.value,
            binBarcode: currentBin.value.ean_code,
        });
    }
});

onBarcodeScanned((barcode, e) => {
    e.preventDefault();
    e.stopPropagation();
    onScanned(barcode);
});
watch(fullyResult, (value) => {
    if (value) {
        onScanned(value);
    }
});

const totalQuantity = page.props.pick_list.lines.reduce(
    (acc, line) => acc + line.quantity,
    0,
);
const scannedQuantity = computed(() =>
    scans.value.reduce((acc, scan) => acc + scan.quantity, 0),
);
const rest = computed(() => totalQuantity - scannedQuantity.value);

watch(currentBinIndex, () => {
    scanned.value = false;
    itemScanned.value = false;
    binScanned.value = false;
    errorMessage.value = null;
});
const next = () => {
    if (currentBinIndex.value < page.props.pick_list.lines.length - 1) {
        currentBinIndex.value++;
    }
};
const previous = () => {
    if (currentBinIndex.value > 0) {
        currentBinIndex.value--;
    }
};
const showPickListModal = ref(false);

const confirm = (bin) => {
    scan(bin.id, remainingQuantity.value);
    if (currentBinIndex.value < page.props.pick_list.lines.length - 1) {
        currentBinIndex.value++;
    }
};

const partialConfirm = (bin) => {
    scan(bin.id, Number(quantity.value));
    if (bin.quantity === scansById.value.get(bin.id)?.sum('quantity')) {
        if (currentBinIndex.value < page.props.pick_list.lines.length - 1) {
            currentBinIndex.value++;
        }
    }
    showQuantityModal.value = false;
};

const quantity = ref('0');
const remainingQuantity = computed(() => {
    return (
        currentBin.value.quantity -
        (scansById.value.get(currentBin.value.id)?.sum('quantity') ?? 0)
    );
});

watch(rest, async (value, oldValue) => {
    if (value === 0) {
        pauseTransactions();
        await submitTransactions();
    } else if (oldValue === 0) {
        resumeTransactions();
    }
});

const showQuantityModal = ref(false);
const promptAmount = (bin) => {
    showQuantityModal.value = true;
    quantity.value = (
        bin.quantity - (scansById.value.get(bin.id)?.sum('quantity') ?? 0)
    ).toString();
};

const scansByPackageId = computed(() => {
    return collect(scans.value).groupBy('pick_package_id');
});

const creatingPackage = ref(false);
const savedPackages = ref([]);
const nextPackage = async () => {
    creatingPackage.value = true;
    try {
        const response = await axios.post(
            route('api.pick.packages.create', {
                pick_list: page.props.pick_list.id,
            }),
        );

        currentPackageId.value = response.data.data.id;
        currentPackageIdentifier.value = response.data.data.identifier;
        savedPackages.value.push(response.data.data);
    } catch (e) {
        console.error(e);
    }
    creatingPackage.value = false;
};

const packages = computed(() => [
    ...page.props.pick_list.packages,
    ...savedPackages.value,
]);

const packagesById = computed(() => {
    return collect(packages.value).keyBy('id');
});

watch(printingErrorMessage, (value) => {
    if (value) {
        flash(value, 'danger');
    }
});

const showTicketModal = ref(false);
const onTicketCreated = (ticketId) => {
    currentBin.value.ticket_id = ticketId;
    showTicketModal.value = false;
};

const beforeReset = () => {
    if (timeData?.data?.['EAN-Code'] === page.props.pick_list.ean_code) {
        stopClock(timeData.data.id);
    }
};
</script>

<template>
    <Head title="Pick"></Head>
    <v-container :class="{ 'tw-p-[16px]': mobile }">
        <AlarmModal
            v-model="scanFailed"
            message="Falscher barcode gescannt!"
        ></AlarmModal>
        <PickTicketModal
            v-model="showTicketModal"
            :stock="page.props.bins"
            :bin="currentBin"
            :ean="page.props.pick_list.ean_code"
            :rest="remainingQuantity"
            :customer-number="page.props.pick_list.customer_number"
            :ticket-type-id="page.props.ticket_type_ids.type_ids.stock_fault"
            :ticket-subtype-id="
                page.props.ticket_type_ids.subtype_ids.stock_fault
            "
            @created="onTicketCreated"
        ></PickTicketModal>
        <AppBarActionsTeleport>
            <v-btn
                :loading="creatingPackage"
                :disabled="
                    (scansByPackageId.get(currentPackageId)?.sum('quantity') ??
                        0) === 0 ||
                    transactionsPending ||
                    rest === 0
                "
                class="tw-mr-2"
                color="secondary"
                :prepend-icon="mdiPlus"
                @click="nextPackage"
                >PAL</v-btn
            >
            <LinkVuetify
                method="delete"
                :show-loading="true"
                :on-before="beforeReset"
                :href="
                    route('pick.reset', { pick_list: page.props.pick_list.id })
                "
                color="danger"
                >Reset</LinkVuetify
            >
        </AppBarActionsTeleport>
        <QuantityModal
            v-model="quantity"
            :show="showQuantityModal"
            @close="showQuantityModal = false"
        >
            <v-btn color="danger" @click="showQuantityModal = false">{{
                $t('Cancel')
            }}</v-btn>
            <v-btn
                :disabled="Number(quantity) > remainingQuantity"
                color="primary"
                @click="partialConfirm(currentBin)"
                >Fertig</v-btn
            >
        </QuantityModal>
        <PickListModal
            v-model="showPickListModal"
            :current-bin-index
            :lines="page.props.pick_list.lines"
            :scans-by-id
            @select="currentBinIndex = $event"
        ></PickListModal>
        <v-row>
            <PackageExpansionPanel
                v-if="packages.length > 1"
                :current-package-identifier
                :packages
                :bins-by-id
                :scans-by-package-id
            ></PackageExpansionPanel>
        </v-row>
        <v-row>
            <v-col :class="{ 'tw-p-0': mobile }">
                <BinCard
                    :packages-by-id
                    :rest
                    :loading="loading"
                    :bin="currentBin"
                    :item-barcode
                    :no-scan="page.props.pick_without_scan"
                    :note="page.props.meta?.note ?? null"
                    :previous-bin
                    :next-bin
                    :barcode-scanned="scanned"
                    :item-scanned="itemScanned"
                    :bin-scanned="binScanned"
                    :current-package-identifier
                    :scans="scansById.get(currentBin.id)"
                    :error-message
                    :stock="page.props.bins"
                    :meta="page.props.meta"
                    @confirm="confirm"
                    @partial-confirm="promptAmount"
                    @open-ticket="showTicketModal = true"
                >
                    <template #buttons>
                        <v-btn
                            v-if="isFully"
                            class="tw-mr-2"
                            @click="fullyScan"
                        >
                            <v-icon
                                size="large"
                                :icon="mdiBarcodeScan"
                            ></v-icon>
                        </v-btn>
                    </template>

                    <v-btn
                        :disabled="previousBin === null"
                        :prepend-icon="mobile ? null : mdiArrowLeft"
                        color="secondary"
                        variant="elevated"
                        @click="previous"
                        ><span v-if="!mobile">Zurück</span>
                        <span v-else
                            ><v-icon :icon="mdiArrowLeft"></v-icon
                        ></span>
                    </v-btn>

                    <div
                        class="tw-ml-2 tw-flex tw-items-center tw-text-2xl tw-font-bold"
                    >
                        {{ currentBinIndex + 1 }} /
                        {{ page.props.pick_list.lines.length }}
                    </div>

                    <v-spacer></v-spacer>
                    <span
                        v-if="rest === 0"
                        class="tw-cursor-pointer tw-rounded-xl tw-border-4 tw-border-solid tw-border-white tw-bg-red-600 tw-p-4 tw-text-lg tw-font-bold tw-uppercase tw-text-white"
                        @click="router.get(route('pick.index'))"
                    >
                        <v-icon
                            class="tw-mb-1"
                            :icon="mdiArrowLeftThick"
                        ></v-icon>
                        <template v-if="!mobile">{{
                            $t('Completed')
                        }}</template>
                        <template v-else
                            ><v-icon
                                size="large"
                                :icon="mdiCheckOutline"
                            ></v-icon
                        ></template>
                    </span>
                    <v-btn color="info" @click="showPickListModal = true">
                        <v-icon :icon="mdiInformation" size="32"></v-icon>
                        <span v-if="!mobile" class="tw-text-lg">Liste</span>
                    </v-btn>
                    <v-btn
                        :disabled="nextBin === null"
                        :append-icon="mobile ? null : mdiArrowRight"
                        color="secondary"
                        variant="elevated"
                        @click="next"
                    >
                        <span v-if="!mobile">Weiter</span>
                        <span v-else
                            ><v-icon :icon="mdiArrowRight"></v-icon
                        ></span>
                    </v-btn>
                </BinCard>
            </v-col>
        </v-row>
        <v-row v-if="page.props.meta?.note">
            <v-col
                :class="{
                    'tw-py-0': !mobile,
                    'tw-px-0': mobile,
                    'tw-pb-0': mobile,
                }"
            >
                <v-card color="danger" variant="tonal" class="tw-text-black">
                    <v-card-title>Anweisung</v-card-title>
                    <v-card-text>{{
                        page.props.meta.note
                    }}</v-card-text></v-card
                >
            </v-col>
        </v-row>
        <v-row>
            <PrintOptions
                v-model="printer"
                :enabled="rest === 0"
            ></PrintOptions>
        </v-row>
    </v-container>
</template>

<style scoped></style>
